// Minimal HTML sanitiser for content authored in RichTextEditor and rendered
// back with dangerouslySetInnerHTML. It is deliberately allow-list based and
// dependency-free; swap for DOMPurify if richer markup is ever needed.

const ALLOWED_TAGS = new Set([
  'A',
  'B',
  'BR',
  'DIV',
  'EM',
  'H1',
  'H2',
  'H3',
  'H4',
  'I',
  'LI',
  'OL',
  'P',
  'S',
  'SPAN',
  'STRIKE',
  'STRONG',
  'U',
  'UL',
]);

const ALLOWED_ATTRIBUTES = new Set(['href', 'target', 'rel', 'style']);

const isSafeUrl = (url: string): boolean =>
  /^(https?:|mailto:|tel:|\/|#)/i.test(url.trim());

/**
 * Strips disallowed elements, every event-handler attribute, and unsafe URLs.
 * Returns an empty string outside the browser (no DOMParser available).
 */
export const sanitizeHtml = (html: string): string => {
  if (!html) return '';
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return '';
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');

  doc.body.querySelectorAll('*').forEach((element) => {
    if (!ALLOWED_TAGS.has(element.tagName)) {
      // Keep the text, drop the element itself.
      element.replaceWith(...Array.from(element.childNodes));
      return;
    }

    Array.from(element.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      if (name.startsWith('on') || !ALLOWED_ATTRIBUTES.has(name)) {
        element.removeAttribute(attribute.name);
        return;
      }
      if (name === 'href' && !isSafeUrl(attribute.value)) {
        element.removeAttribute(attribute.name);
      }
    });

    if (element.tagName === 'A') {
      element.setAttribute('target', '_blank');
      element.setAttribute('rel', 'noopener noreferrer');
    }
  });

  return doc.body.innerHTML;
};
